import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function HybridTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Header Band */}
          <div className={`h-3 bg-gradient-to-r ${colors.headerBg}`}></div>

          <div className="p-8">
            {/* Header */}
            <div className={`text-center mb-6 pb-6 border-b-2 ${colors.sectionBorder}`}>
              <h1 className={`text-4xl font-bold ${colors.sectionTitle} mb-2`}>{personalInfo.fullName || 'YOUR NAME'}</h1>
              <div className={`text-lg ${colors.accentText} mb-3`}>{personalInfo.email}</div>
              <div className="flex justify-center flex-wrap gap-4 text-sm text-gray-600">
                {personalInfo.phone && <span>{personalInfo.phone}</span>}
                {personalInfo.address && <span>|</span>}
                {personalInfo.address && <span>{personalInfo.address}</span>}
                {personalInfo.linkedin && <span>|</span>}
                {personalInfo.linkedin && <span>{personalInfo.linkedin}</span>}
              </div>
            </div>

            {/* Summary */}
            {summary && (
              <div className="mb-6">
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 flex items-center`}>
                  <div className={`w-2 h-6 ${colors.accentBg} mr-3`}></div>
                  {labels.professionalSummary}
                </h2>
                <p className="text-gray-700 leading-relaxed pl-5">{summary}</p>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-4 flex items-center`}>
                  <div className={`w-2 h-6 ${colors.accentBg} mr-3`}></div>
                  {labels.workExperience}
                </h2>
                <div className="pl-5 space-y-4">
                  {experience.map((exp: any) => (
                    <div key={exp.id} className="relative pl-6">
                      <div className={`absolute left-0 top-2 w-3 h-3 ${colors.timelineDot} rounded-full`}></div>
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                        <span className="text-sm text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                      </div>
                      <div className={`text-sm font-semibold ${colors.accentText} mb-2`}>{exp.company}</div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Two Columns */}
            <div className="grid grid-cols-2 gap-6">
              {/* Skills */}
              {skills && skills.length > 0 && (
                <div>
                  <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 flex items-center`}>
                    <div className={`w-2 h-6 ${colors.accentBg} mr-3`}></div>
                    {labels.skills}
                  </h2>
                  <div className="pl-5 space-y-3">
                    {skills.map((skillGroup: any, idx: number) => (
                      <div key={idx}>
                        <div className={`text-sm font-bold ${colors.accentText} mb-2`}>{skillGroup.category}</div>
                        <div className="flex flex-wrap gap-2">
                          {skillGroup.skills?.map((skill: string, i: number) => (
                            <span key={i} className={`text-xs ${colors.badgeBg} ${colors.badgeText} px-3 py-1 rounded-full border ${colors.sectionBorder}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {education && education.length > 0 && (
                <div>
                  <h2 className={`text-xl font-bold ${colors.sectionTitle} mb-3 flex items-center`}>
                    <div className={`w-2 h-6 ${colors.accentBg} mr-3`}></div>
                    {labels.education}
                  </h2>
                  <div className="pl-5 space-y-3">
                    {education.map((edu: any) => (
                      <div key={edu.id}>
                        <div className={`text-sm font-bold ${colors.sectionTitle}`}>{edu.degree}</div>
                        <div className={`text-sm ${colors.accentText}`}>{edu.institution}</div>
                        <div className="text-sm text-gray-600">{edu.graduationDate}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  )
}
